import { Link } from 'react-router-dom';
import { HardDrive, Sparkles, Archive, MonitorSmartphone, BellRing, ArrowRight, Plus, CreditCard } from 'lucide-react';
import type { Entitlements, NotificationUsage, PortalAppliance, UsageSummary } from '@scada-ink/shared/cloud';
import { Button } from '@/components/ui';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, ConnectionBadge, EmptyState, HealthLabel, LoadError, Loading, Meter, PortalHeader, StatCard, timeAgo } from './components';

interface OverviewData {
  appliances: PortalAppliance[];
  entitlements: Entitlements;
  ai: UsageSummary;
  notify: NotificationUsage;
}

function ServiceTile({ icon: Icon, title, active, detail, to }: { icon: typeof Sparkles; title: string; active: boolean; detail: string; to: string }) {
  return (
    <Link to={to} className="group glass-card p-4 flex items-start gap-3 hover:shadow-md transition-shadow">
      <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-brand-primary/10 text-brand-primary' : 'bg-ink-muted text-text-muted'}`}>
        <Icon size={18} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold group-hover:text-brand-primary">{title}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wide ${active ? 'text-status-running' : 'text-text-muted'}`}>{active ? 'Active' : 'Off'}</span>
        </span>
        <span className="block text-xs text-text-secondary mt-0.5">{detail}</span>
      </span>
    </Link>
  );
}

export function OverviewPage() {
  usePageTitle('Portal');
  const { session } = useSession();
  const { data, error, loading, reload } = useAsync<OverviewData>(async () => {
    const [appliances, entitlements, ai, notify] = await Promise.all([
      cloudApi.get<PortalAppliance[]>('/api/portal/appliances'),
      cloudApi.get<Entitlements>('/api/portal/entitlements'),
      cloudApi.get<UsageSummary>('/api/portal/usage'),
      cloudApi.get<NotificationUsage>('/api/portal/notifications/usage'),
    ]);
    return { appliances, entitlements, ai, notify };
  });

  const firstName = session?.user.name.split(' ')[0] ?? '';

  if (loading && !data) return <Loading />;
  if (error || !data) return <LoadError message={error ?? 'Could not load your portal.'} onRetry={reload} />;

  const { appliances, entitlements: ent, ai, notify } = data;
  const online = appliances.filter((a) => a.status === 'online').length;
  const attention = appliances.filter((a) => a.health === 'warning' || a.health === 'critical');
  const anyService = ent.ai.active || ent.archive.active || ent.remote.active || ent.notifications.active;

  return (
    <>
      <PortalHeader
        title={`Welcome back${firstName ? `, ${firstName}` : ''}`}
        description={`${session?.account.company} · Scada.Ink Cloud`}
        actions={
          <>
            <Button to="/portal/appliances?claim=1" variant="secondary" size="sm">
              <Plus size={14} /> Add appliance
            </Button>
            <Button to="/portal/billing" size="sm">
              <CreditCard size={14} /> Plans
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Appliances" value={appliances.length} sub={`${online} online`} />
        <StatCard label="Need attention" value={attention.length} tone={attention.length ? 'warn' : 'good'} sub={attention.length ? 'Warnings or critical' : 'All healthy'} />
        <StatCard label="AI requests this month" value={ai.ai.requests} sub={ent.ai.active ? `of ${ent.ai.requestsPerMonth} included` : 'No AI plan'} />
        <StatCard label="SMS this month" value={notify.sms.used} sub={ent.notifications.active ? `of ${notify.sms.included} included` : 'No alerts plan'} />
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        <Card
          title="Appliance health"
          actions={
            <Link to="/portal/appliances" className="text-xs font-semibold text-brand-primary inline-flex items-center gap-1">
              All appliances <ArrowRight size={12} />
            </Link>
          }
          padded={appliances.length === 0}
        >
          {appliances.length === 0 ? (
            <EmptyState
              icon={HardDrive}
              title="No appliances yet"
              body="Appliances appear here when you buy or store a license for them. Turn on Cloud health reporting on the appliance to see its status."
              action={
                <Button to="/portal/appliances?claim=1" size="sm">
                  Add your first appliance
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-glass-border">
              {[...appliances]
                .sort((a, b) => ['critical', 'warning', 'unknown', 'healthy'].indexOf(a.health) - ['critical', 'warning', 'unknown', 'healthy'].indexOf(b.health))
                .slice(0, 6)
                .map((a) => (
                  <li key={a.id}>
                    <Link to={`/portal/appliances/${a.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-ink-muted/50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{a.name}</div>
                        <div className="text-xs text-text-muted truncate">
                          {a.site || 'No site'} · last report {timeAgo(a.lastSeenAt)}
                          {a.healthReasons[0] ? ` · ${a.healthReasons[0]}` : ''}
                        </div>
                      </div>
                      <HealthLabel health={a.health} />
                      <ConnectionBadge status={a.status} />
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </Card>

        <div className="space-y-6">
          <Card title="Cloud services">
            <div className="grid gap-2.5">
              <ServiceTile icon={Sparkles} title="Smart Assist AI" active={ent.ai.active} detail={ent.ai.active ? `${ent.ai.requestsPerMonth} requests / month` : 'Subscribe to enable AI'} to="/portal/ai" />
              <ServiceTile icon={Archive} title="Data Archive" active={ent.archive.active} detail={ent.archive.active ? `${ent.archive.appliances} appliance(s) covered` : 'Off-site history'} to="/portal/archive" />
              <ServiceTile icon={MonitorSmartphone} title="Remote Viewing" active={ent.remote.active} detail={ent.remote.active ? `${ent.remote.appliances} appliance(s) covered` : 'View from anywhere'} to="/portal/remote" />
              <ServiceTile icon={BellRing} title="SMS & Voice" active={ent.notifications.active} detail={ent.notifications.active ? `${notify.sms.included} SMS · ${notify.voice.included} min` : 'Twilio alerts'} to="/portal/notifications" />
            </div>
            {!anyService && (
              <div className="mt-4">
                <Button to="/portal/billing" size="sm" className="w-full" arrow>
                  Browse plans
                </Button>
              </div>
            )}
          </Card>

          {(ent.ai.active || ent.notifications.active) && (
            <Card title="This month">
              <div className="space-y-4">
                {ent.ai.active && <Meter label="AI requests" used={ai.ai.requests} max={ai.ai.included.requests} />}
                {ent.ai.active && <Meter label="AI page designs" used={ai.ai.pageDesigns} max={ai.ai.included.pageDesigns} />}
                {ent.notifications.active && <Meter label="SMS" used={notify.sms.used} max={notify.sms.cap ?? notify.sms.included} />}
                {ent.notifications.active && <Meter label="Voice" used={notify.voice.used} max={notify.voice.cap ?? notify.voice.included} unit="min" />}
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
